import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { formatDateTimeISO, formatDateISO, formatFileSize } from '../lib/dateUtils';

interface FileWithSession {
  id: string;
  file_name: string;
  file_type: 'audio' | 'text' | 'document' | 'transcript';
  file_size: number;
  storage_path: string;
  created_at: string;
  uploaded_by_name: string;
  session_id: string;
  session_date: string;
  aircraft_type: string;
  trainee_name: string;
}

export default function Files() {
  const { user } = useAuth();
  const [files, setFiles] = useState<FileWithSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [fileTypeFilter, setFileTypeFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'date' | 'name' | 'size'>('date');

  const userRole = user?.user_metadata?.role || 'trainee';

  // Load files from database with session context
  const loadFiles = async () => {
    if (!user) return;

    setLoading(true);
    try {
      let query = supabase
        .from('files')
        .select(`
          id,
          file_name,
          file_type,
          file_size,
          storage_path,
          created_at,
          uploaded_by_user:uploaded_by(full_name),
          session:session_id(
            id,
            session_date,
            aircraft_type,
            trainee:trainee_id(full_name)
          )
        `)
        .order('created_at', { ascending: false });

      // Role-based filtering
      if (userRole === 'instructor') {
        // Instructors see files from their sessions
        const { data: instructorSessions } = await supabase
          .from('sessions')
          .select('id')
          .eq('instructor_id', user.id);

        const sessionIds = instructorSessions?.map(s => s.id) || [];
        if (sessionIds.length > 0) {
          query = query.in('session_id', sessionIds);
        }
      } else if (userRole === 'trainee') {
        // Trainees see files from their sessions
        const { data: traineeSessions } = await supabase
          .from('sessions')
          .select('id')
          .eq('trainee_id', user.id);

        const sessionIds = traineeSessions?.map(s => s.id) || [];
        if (sessionIds.length > 0) {
          query = query.in('session_id', sessionIds);
        }
      }
      // Admin sees all files (no filter)

      const { data, error } = await query;

      if (error) throw error;

      // Transform the data
      const transformedFiles: FileWithSession[] = (data || []).map((file: any) => ({
        id: file.id,
        file_name: file.file_name,
        file_type: file.file_type,
        file_size: file.file_size,
        storage_path: file.storage_path,
        created_at: file.created_at,
        uploaded_by_name: Array.isArray(file.uploaded_by_user)
          ? file.uploaded_by_user[0]?.full_name || 'Unknown'
          : file.uploaded_by_user?.full_name || 'Unknown',
        session_id: file.session?.id || '',
        session_date: file.session?.session_date || '',
        aircraft_type: file.session?.aircraft_type || 'Unknown',
        trainee_name: Array.isArray(file.session?.trainee)
          ? file.session.trainee[0]?.full_name || 'Unknown'
          : file.session?.trainee?.full_name || 'Unknown',
      }));

      setFiles(transformedFiles);
    } catch (error) {
      console.error('Error loading files:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFiles();
  }, [user]);

  // Download file
  const handleDownload = async (file: FileWithSession) => {
    try {
      const { data, error } = await supabase.storage
        .from('session-files')
        .download(file.storage_path);

      if (error) throw error;

      // Create download link
      const url = URL.createObjectURL(data);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file_name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      alert('Failed to download file');
    }
  };

  // Delete file
  const handleDelete = async (file: FileWithSession) => {
    if (!confirm(`Are you sure you want to delete "${file.file_name}"?`)) return;

    setDeletingFileId(file.id);
    try {
      // Delete from storage
      const { error: storageError } = await supabase.storage
        .from('session-files')
        .remove([file.storage_path]);

      if (storageError) throw storageError;

      // Delete from database
      const { error: dbError } = await supabase
        .from('files')
        .delete()
        .eq('id', file.id);

      if (dbError) throw dbError;

      // Remove from local state
      setFiles((prev) => prev.filter((f) => f.id !== file.id));
      alert('File deleted successfully');
    } catch (error) {
      console.error('Error deleting file:', error);
      alert('Failed to delete file');
    } finally {
      setDeletingFileId(null);
    }
  };

  // Get file icon
  const getFileIcon = (type: string) => {
    switch (type) {
      case 'audio':
        return '🎵';
      case 'text':
      case 'transcript':
        return '📄';
      case 'document':
        return '📎';
      default:
        return '📎';
    }
  };

  // Filter and sort files
  const filteredFiles = files.filter((file) => {
    const matchesSearch =
      searchTerm === '' ||
      file.file_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.trainee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      file.aircraft_type.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesType = fileTypeFilter === 'all' || file.file_type === fileTypeFilter;

    return matchesSearch && matchesType;
  });

  const sortedFiles = [...filteredFiles].sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case 'name':
        return a.file_name.localeCompare(b.file_name);
      case 'size':
        return b.file_size - a.file_size;
      default:
        return 0;
    }
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-semibold text-navy mb-2">📁 All Files Dashboard</h2>
        <p className="text-steel">
          Browse and manage all files across your training sessions
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-steel">Total Files</p>
              <p className="text-2xl font-bold text-navy mt-1">{files.length}</p>
            </div>
            <div className="text-3xl">📎</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-steel">Audio Files</p>
              <p className="text-2xl font-bold text-navy mt-1">
                {files.filter(f => f.file_type === 'audio').length}
              </p>
            </div>
            <div className="text-3xl">🎵</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-steel">Transcripts</p>
              <p className="text-2xl font-bold text-navy mt-1">
                {files.filter(f => f.file_type === 'text' || f.file_type === 'transcript').length}
              </p>
            </div>
            <div className="text-3xl">📄</div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-steel">Total Storage</p>
              <p className="text-2xl font-bold text-navy mt-1">
                {formatFileSize(files.reduce((sum, f) => sum + f.file_size, 0))}
              </p>
            </div>
            <div className="text-3xl">💾</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-navy mb-4">Filters & Search</h3>
        
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by filename, trainee, or aircraft..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
            />
          </div>

          {/* File Type Filter */}
          <div className="w-full md:w-48">
            <select
              value={fileTypeFilter}
              onChange={(e) => setFileTypeFilter(e.target.value)}
              className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
            >
              <option value="all">All Types</option>
              <option value="audio">Audio</option>
              <option value="text">Text</option>
              <option value="transcript">Transcript</option>
              <option value="document">Document</option>
            </select>
          </div>

          {/* Sort By */}
          <div className="w-full md:w-48">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as 'date' | 'name' | 'size')}
              className="w-full px-3 py-2 bg-white border border-steel/30 rounded-md placeholder-steel/50 text-navy focus:outline-none focus:ring-2 focus:ring-orca-elevate focus:border-orca-elevate"
            >
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="size">Sort by Size</option>
            </select>
          </div>
        </div>

        {/* Active Filters Summary */}
        {(searchTerm || fileTypeFilter !== 'all') && (
          <div className="mt-3 flex items-center gap-2 text-sm text-steel">
            <span>Active filters:</span>
            {searchTerm && (
              <span className="px-2 py-1 bg-orca-elevate/10 text-orca-elevate rounded-md">
                Search: "{searchTerm}"
              </span>
            )}
            {fileTypeFilter !== 'all' && (
              <span className="px-2 py-1 bg-orca-elevate/10 text-orca-elevate rounded-md">
                Type: {fileTypeFilter}
              </span>
            )}
            <button
              onClick={() => {
                setSearchTerm('');
                setFileTypeFilter('all');
              }}
              className="text-red-600 hover:text-red-700 font-medium"
            >
              Clear All
            </button>
          </div>
        )}
      </div>

      {/* Info Box for Upload Instructions */}
      <div className="bg-blue-50 border-l-4 border-orca-elevate rounded-lg p-4">
        <div className="flex items-start">
          <div className="text-2xl mr-3">ℹ️</div>
          <div>
            <h4 className="font-semibold text-navy mb-1">How to Upload Files</h4>
            <p className="text-sm text-steel">
              Files can only be uploaded within a session context. Go to{' '}
              <Link to="/sessions" className="text-orca-elevate hover:text-orca-elevate/80 font-medium">
                Training Sessions
              </Link>
              , open a session, and upload files directly to that session.
            </p>
          </div>
        </div>
      </div>

      {/* Files List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-navy">
            Files ({sortedFiles.length} {sortedFiles.length === 1 ? 'file' : 'files'})
          </h3>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orca-elevate"></div>
            <p className="mt-2 text-steel">Loading files...</p>
          </div>
        ) : sortedFiles.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg">
            <div className="text-5xl mb-3">📁</div>
            {files.length === 0 ? (
              <>
                <p className="text-steel mb-2">No files found</p>
                <p className="text-sm text-steel/70">
                  Upload files by going to{' '}
                  <Link to="/sessions" className="text-orca-elevate hover:text-orca-elevate/80 font-medium">
                    Training Sessions
                  </Link>
                </p>
              </>
            ) : (
              <>
                <p className="text-steel mb-2">No files match your filters</p>
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setFileTypeFilter('all');
                  }}
                  className="text-sm text-orca-elevate hover:text-orca-elevate/80 font-medium"
                >
                  Clear filters
                </button>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {sortedFiles.map((file) => (
              <div
                key={file.id}
                className="flex items-start justify-between p-4 border border-steel/20 rounded-md hover:bg-ice/50 transition-colors"
              >
                {/* File Info */}
                <div className="flex items-start space-x-4 flex-1 min-w-0">
                  <span className="text-3xl flex-shrink-0">{getFileIcon(file.file_type)}</span>
                  <div className="flex-1 min-w-0">
                    {/* File Name */}
                    <p className="text-sm font-semibold text-navy truncate mb-1">
                      {file.file_name}
                    </p>

                    {/* File Metadata */}
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-steel mb-2">
                      <span>{formatFileSize(file.file_size)}</span>
                      <span>•</span>
                      <span className="capitalize">{file.file_type}</span>
                      <span>•</span>
                      <span>{formatDateTimeISO(file.created_at)}</span>
                      <span>•</span>
                      <span>Uploaded by {file.uploaded_by_name}</span>
                    </div>

                    {/* Session Context */}
                    <div className="flex items-center space-x-2 text-xs">
                      <span className="px-2 py-0.5 bg-orca-elevate/10 text-orca-elevate rounded-md font-medium">
                        📝 Session: {formatDateISO(file.session_date)}
                      </span>
                      <span className="px-2 py-0.5 bg-purple-100 text-purple-700 rounded-md">
                        👤 {file.trainee_name}
                      </span>
                      <span className="px-2 py-0.5 bg-steel/10 text-steel rounded-md">
                        ✈️ {file.aircraft_type}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex flex-col items-end space-y-2 ml-4 flex-shrink-0">
                  <Link
                    to={`/sessions/${file.session_id}`}
                    className="px-3 py-1 text-xs font-medium text-white bg-orca-elevate hover:bg-orca-elevate/90 rounded-md transition-colors"
                  >
                    View Session
                  </Link>
                  <button
                    onClick={() => handleDownload(file)}
                    className="px-3 py-1 text-xs font-medium text-orca-elevate hover:text-orca-elevate/80 transition-colors"
                  >
                    Download
                  </button>
                  <button
                    onClick={() => handleDelete(file)}
                    disabled={deletingFileId === file.id}
                    className="px-3 py-1 text-xs font-medium text-red-600 hover:text-red-700 transition-colors disabled:opacity-50"
                  >
                    {deletingFileId === file.id ? 'Deleting...' : 'Delete'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}



